import {
  AURA_SLOTS,
  auraLevel,
  BAG_SIZE,
  BODY_SLOTS,
  type BodySlot,
  carriedEntries,
  type ClassId,
  CLASSES,
  coveredSlot,
  type ItemSlot,
  isCosmeticSlot,
  ITEM_SLOTS,
  isBetterThanWorn,
  type Equipped,
  affixTotals,
  effectiveDps,
  equipmentBonus,
  getItem,
  type InventoryState,
  rarityRank,
  type ItemInstance,
  type ItemTemplate,
  mitigation,
  type ProgressEvent,
  SHINE_FROM_LEVEL,
  statsFor,
  upgradeAverage,
  upgradeColor,
  wornPieces,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { type AtlasFrame, DollPreview } from './doll-preview.js';
import { BagDestroyButton } from './bag-destroy-button.js';
import { BagSortButton } from './bag-sort-button.js';
import { isAction } from '../settings/keybindings.js';
import { combatLines, combatOfEquipped } from './combat-lines.js';
import { closeButton, hudShortcutButton } from './hud.js';
import { addBetterArrow, addLevelBadge, paintItemIcon } from './item-icon.js';
import { element, kitChip, kitHeading, kitLines, kitSeal, kitStage, kitTitle } from './kit.js';
import { uiDecimal, uiFormat, uiNumber, uiText } from './text.js';
import { compareWithWorn } from './item-compare.js';
import { confirmItemDestruction } from './destroy-item-modal.js';
import { formatDps, ItemTooltip, type TooltipAction, type TooltipDetails } from './item-tooltip.js';
import { isTouch } from './touch.js';
import './inventory.css';

/**
 * Profile and inventory: the paper doll with its six slots and a live
 * preview, the stats the server would compute, and the 30-slot bag with drag
 * & drop. Every action is an intent sent through the callbacks; the panel
 * redraws from the inventory the server sends back, never from its own guess.
 *
 * On a phone (ADR 0043) nothing hovers, drags or double-clicks: a tap pins the
 * item's card with buttons for what those gestures do (equip, remove, use,
 * move to another case, hand over one or the whole stack), the same intents.
 * A small screen shows the doll and the bag one at a time, behind two tabs.
 *
 * The look side of the doll (ADR 0072) shows the wardrobe instead of the bag:
 * every cosmetic and companion owned, as many as there are, and nothing else.
 *
 * A bag item is destroyed (ADR 0084) through « Détruire » beside the bag, or
 * the card's button on a phone, always behind a confirmation window.
 */

const BODY_LABELS: Record<ItemSlot, string> = {
  helmet: 'Tête',
  chest: 'Torse',
  weapon: 'Arme',
  offhand: 'Gauche',
  gloves: 'Mains',
  boots: 'Pieds',
  pet: 'Familier',
  mount: 'Monture',
  wings: 'Ailes',
  trail: 'Traînée',
};

/**
 * Where each body slot sits on the 3×4 doll grid (column, row), around the
 * preview: the armour head to foot on the left, what the hands hold on the
 * right, the companions under it. A cosmetic place (ADR 0031) sits where
 * the slot it covers does: the doll shows one side or the other. Wings
 * (ADR 0053) have a look place only, under the preview between the companions,
 * and the footstep trail (ADR 0073) right under the preview, above the wings.
 */
const BODY_LAYOUT: Record<ItemSlot, [number, number]> = {
  helmet: [1, 1],
  chest: [1, 2],
  boots: [1, 3],
  weapon: [3, 1],
  offhand: [3, 2],
  gloves: [3, 3],
  pet: [1, 4],
  mount: [3, 4],
  wings: [2, 4],
  trail: [2, 3],
};

/** The two faces of the doll: what fights, and what shows over it. */
type DollSide = 'gear' | 'look';

/** On a small screen, the half of the window that shows (CSS only acts on it there). */
type Page = 'doll' | 'bag';

function slotLabel(slot: BodySlot): string {
  return isCosmeticSlot(slot) ? BODY_LABELS[coveredSlot(slot)] : BODY_LABELS[slot];
}

function layoutOf(slot: BodySlot): [number, number] {
  return isCosmeticSlot(slot) ? BODY_LAYOUT[coveredSlot(slot)] : BODY_LAYOUT[slot];
}

/** How long a precious item waits for the second click that picks it. */
const PICK_CONFIRM_MS = 3000;

type DragSource =
  | { from: 'bag'; slot: number; itemId: string }
  | { from: 'wardrobe'; itemId: string }
  | { from: 'body'; slot: BodySlot };

/** The least the wardrobe shows: one row of cases, so an empty one still reads as a place. */
const WARDROBE_MIN_CASES = 6;
const WARDROBE_COLUMNS = 6;

/**
 * Another window taking items from the bag (a trade): a click on a bag item
 * hands it over instead of dragging it.
 */
export interface PickMode {
  /** The verb of the hint under the bag (« Échanger »). */
  label: string;
  /** One of the stack by default, the whole stack with Shift. */
  onPick: (itemId: string, quantity: number) => void;
  /** Which bag items answer the click; the others stay dim. All of them by default. */
  canPick?: (instance: ItemInstance, template: ItemTemplate) => boolean;
}

export interface InventoryPanelOptions {
  classId: ClassId;
  bodyKey: string;
  frame: (name: string) => AtlasFrame | null;
  featureTints: { eyes: string; hair: string; skin: string };
  hairStyle?: string;
  body?: string;
  onEquip: (itemId: string) => void;
  onUnequip: (slot: BodySlot) => void;
  onMove: (from: number, to: number) => void;
  /** The « Ranger » button: tidy the bag (the server decides how). */
  onSort: () => void;
  onUse: (itemId: string) => void;
  /** Destroy part or all of a bag stack, once the player confirmed. */
  onDestroy: (itemId: string, quantity: number) => void;
  /** Shift+click, or « Lier dans le chat » on a card: the item goes into the chat's line (ADR 0085). */
  onLink?: (item: ItemInstance) => void;
  /** Item icons, packed; without it the panel falls back to the item's first letters. */
  icons?: CssAtlas | null;
}

export class InventoryPanel {
  private readonly root: HTMLElement;
  private readonly bagGrid: HTMLElement;
  private readonly wardrobeGrid: HTMLElement;
  private readonly wardrobeHint: HTMLElement;
  private readonly statsBlock: HTMLElement;
  private readonly purse: HTMLElement;
  private readonly bagHeading: HTMLElement;
  private readonly sortButton: BagSortButton;
  private readonly destroyButton: BagDestroyButton;
  /** Armed by « Détruire »: the next bag item clicked is the one to destroy. */
  private destroying = false;
  private readonly onDestroyOutside = (event: PointerEvent): void => {
    const target = event.target;
    if (target instanceof Node && this.destroyButton.element.contains(target)) return;
    if (target instanceof Element && target.closest('.item[data-from="bag"]') !== null) return;
    this.endDestroy();
  };
  private readonly seal: HTMLElement;
  private readonly bodyCells = new Map<BodySlot, HTMLElement>();
  private readonly preview: DollPreview;
  private readonly tooltip = new ItemTooltip();
  /** Set while another window takes from the bag: where a click on a bag item goes. */
  private pick: PickMode | null = null;
  private readonly pickHint: HTMLElement;
  /** The precious item waiting for its second click, and until when. */
  private armed: { itemId: string; until: number } | undefined;
  private readonly button: HTMLButtonElement;
  private readonly sideTabs = new Map<DollSide, HTMLButtonElement>();
  private side: DollSide = 'gear';
  private readonly pageTabs = new Map<Page, HTMLButtonElement>();
  /** The item whose card a tap pinned: its bag item id, or `body:<slot>`. */
  private selected: string | null = null;
  /** The bag case of the item being moved by touch, waiting for the case it goes to. */
  private moving: number | null = null;
  private readonly onMoveOutside = (event: PointerEvent): void => {
    if (event.target instanceof Node && this.bagGrid.contains(event.target)) return;
    this.endMove();
  };
  private inventory: InventoryState = {
    bagSize: BAG_SIZE,
    bag: [],
    wardrobe: [],
    equipped: {},
    gold: 0,
  };
  private progress: ProgressEvent | undefined;
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !this.root.hidden) {
      if (this.moving !== null) {
        this.endMove();
        return;
      }
      if (this.destroying) {
        this.endDestroy();
        return;
      }
      this.toggle(false);
      return;
    }
    if (!isAction(event, 'inventory') || event.repeat) return;
    if (document.activeElement instanceof HTMLInputElement) return;
    event.preventDefault();
    this.toggle();
  };

  constructor(private readonly options: InventoryPanelOptions) {
    this.root = document.createElement('section');
    this.root.className = 'inventory';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Inventaire et profil'));

    // The title reads like the profile's: the level's seal, the window, the class.
    const title = document.createElement('h2');
    title.className = 'inventory__title';
    kitTitle(title, 'inventory');
    this.seal = element('span', 'inventory__seal');
    title.append(
      this.seal,
      element('span', 'inventory__name', 'Inventaire'),
      element('span', 'inventory__class', CLASSES[options.classId].name),
    );
    const close = closeButton('Fermer (Échap)', () => {
      this.toggle(false);
    });
    title.append(close);

    // Above the doll, its two faces: the gear that fights, the look worn
    // over it (ADR 0031). Companions are looks already and stay on both.
    const sides = document.createElement('div');
    sides.className = 'doll__sides';
    sides.setAttribute('role', 'tablist');
    for (const [side, label] of [
      ['gear', 'Équipement'],
      ['look', 'Apparence'],
    ] as const) {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'doll__side';
      tab.setAttribute('role', 'tab');
      tab.textContent = uiText(label);
      tab.addEventListener('click', () => {
        this.showSide(side);
      });
      this.sideTabs.set(side, tab);
      sides.append(tab);
    }

    const doll = document.createElement('div');
    doll.className = 'doll';
    this.preview = new DollPreview({
      bodyKey: options.bodyKey,
      frame: options.frame,
      featureTints: options.featureTints,
      hairStyle: options.hairStyle,
      body: options.body,
      scale: 3,
    });
    // The character stands in the meadow, as on the profile.
    const stage = kitStage('doll__stage');
    stage.append(this.preview.canvas);
    doll.append(stage);
    for (const slot of BODY_SLOTS) {
      const cell = document.createElement('div');
      cell.className = isCosmeticSlot(slot) ? 'slot slot--body slot--look' : 'slot slot--body';
      cell.dataset.label = uiText(slotLabel(slot));
      const [column, row] = layoutOf(slot);
      cell.style.gridColumn = String(column);
      cell.style.gridRow = String(row);
      this.bindDropTarget(cell, { kind: 'body', slot });
      this.bodyCells.set(slot, cell);
      doll.append(cell);
    }

    this.bagGrid = document.createElement('div');
    this.bagGrid.className = 'bag';
    this.wardrobeGrid = document.createElement('div');
    this.wardrobeGrid.className = 'bag bag--wardrobe';
    // Anything worn dropped on the wardrobe comes off into it.
    this.bindDropTarget(this.wardrobeGrid, { kind: 'wardrobe' });
    this.wardrobeHint = element(
      'p',
      'inventory__hint',
      'Tes cosmétiques et tes compagnons se rangent ici, sans limite de place.',
    );
    this.statsBlock = document.createElement('div');
    this.statsBlock.className = 'stats';

    // The owner's gold (ADR 0016) sits under the bag, where the loot it
    // buys and sells lies, rather than among the stats it does not change.
    this.purse = element('div', 'purse');
    this.bagHeading = element('div', 'inventory__heading');
    this.sortButton = new BagSortButton(options.onSort);
    this.destroyButton = new BagDestroyButton({
      onArm: () => {
        if (this.destroying) this.endDestroy();
        else this.startDestroy();
      },
      onDrop: (itemId) => {
        this.askDestroy(itemId);
      },
    });

    const left = document.createElement('div');
    left.className = 'inventory__doll-side';
    left.append(sides, doll, this.statsBlock);
    // Under the bag, what a click does while another window takes from it.
    this.pickHint = document.createElement('p');
    this.pickHint.className = 'inventory__hint';
    this.pickHint.hidden = true;

    const right = document.createElement('div');
    right.className = 'inventory__side';
    right.append(
      this.bagHeading,
      this.bagGrid,
      this.wardrobeGrid,
      this.wardrobeHint,
      this.pickHint,
      this.purse,
    );

    // Too small a screen for both halves side by side: one at a time.
    const pages = document.createElement('div');
    pages.className = 'inventory__pages';
    pages.setAttribute('role', 'tablist');
    for (const [page, label] of [
      ['doll', 'Personnage'],
      ['bag', 'Sac'],
    ] as const) {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'inventory__page';
      tab.dataset.page = page;
      tab.setAttribute('role', 'tab');
      tab.textContent = uiText(label);
      tab.addEventListener('click', () => {
        this.showPage(page);
      });
      this.pageTabs.set(page, tab);
      pages.append(tab);
    }
    this.root.append(title, pages, left, right);
    document.body.append(this.root);

    this.button = hudShortcutButton('inventory', 'Inventaire', 'inventory');
    this.button.addEventListener('click', () => {
      this.toggle();
    });

    window.addEventListener('keydown', this.onKeyDown);
    this.showPage('bag');
    this.showSide('gear');
  }

  private showPage(page: Page): void {
    this.tooltip.hide();
    this.endMove();
    for (const [id, tab] of this.pageTabs) {
      tab.classList.toggle('inventory__page--active', id === page);
      tab.setAttribute('aria-selected', String(id === page));
    }
    this.root.classList.toggle('inventory--page-doll', page === 'doll');
    this.root.classList.toggle('inventory--page-bag', page === 'bag');
  }

  /** Turn the doll to its look side: where a piece just bought waits to be worn. */
  showLook(): void {
    this.showPage('doll');
    this.showSide('look');
  }

  /** Which face of the doll shows: the cells of the other are hidden, not removed. */
  private showSide(side: DollSide): void {
    this.side = side;
    for (const [id, tab] of this.sideTabs) {
      tab.classList.toggle('doll__side--active', id === side);
      tab.setAttribute('aria-selected', String(id === side));
    }
    for (const [slot, cell] of this.bodyCells) {
      const isCompanion = slot === 'pet' || slot === 'mount';
      cell.hidden = !isCompanion && isCosmeticSlot(slot) !== (side === 'look');
    }
    // Beside the look, the wardrobe; beside the gear, the bag.
    this.bagGrid.hidden = side === 'look';
    this.wardrobeGrid.hidden = side !== 'look';
    this.pageTabs.get('bag')?.replaceChildren(uiText(side === 'look' ? 'Garde-robe' : 'Sac'));
    this.endMove();
    this.endDestroy();
    this.render();
  }

  /**
   * While another window takes from the bag: a click on a bag item hands one
   * of it over, Shift+click the whole stack; a piece the smith worked on, or
   * an epic and better, waits for a second click. `null` ends it.
   */
  setPickMode(mode: PickMode | null): void {
    this.pick = mode;
    this.armed = undefined;
    this.tooltip.hide();
    this.endMove();
    this.endDestroy();
    this.root.classList.toggle('inventory--picking', mode !== null);
    // What another window takes lies in the bag: a small screen turns to it.
    if (mode !== null) this.showPage('bag');
    this.showPickHint();
    this.render();
  }

  private showPickHint(): void {
    const mode = this.pick;
    this.pickHint.hidden = mode === null;
    if (mode === null) return;
    const label = uiText(mode.label);
    this.pickHint.textContent = isTouch()
      ? uiFormat('{label} : touche un objet du sac.', { label })
      : uiFormat('{label} : clique un objet du sac (Maj : toute la pile).', { label });
  }

  /** The item icons this panel paints with, for a window that lists the same items. */
  get icons(): CssAtlas | null {
    return this.options.icons ?? null;
  }

  toggle(open = this.root.hidden): void {
    this.root.hidden = !open;
    this.tooltip.hide();
    this.endMove();
    this.endDestroy();
    if (open) this.render();
  }

  setInventory(inventory: InventoryState): void {
    this.inventory = inventory;
    this.render();
  }

  setProgress(progress: ProgressEvent): void {
    this.progress = progress;
    this.render();
  }

  private wearer(): { level: number; classId: ClassId } {
    return { level: this.progress?.level ?? 1, classId: this.options.classId };
  }

  private render(): void {
    if (this.root.hidden) return;
    const equipped = this.inventory.equipped as Equipped;

    for (const [slot, cell] of this.bodyCells) {
      cell.replaceChildren();
      const instance = equipped[slot];
      if (instance !== undefined) {
        cell.append(this.itemElement(instance, { from: 'body', slot }));
      }
    }

    this.bagGrid.replaceChildren();
    const bySlot = new Map(this.inventory.bag.map((entry) => [entry.slot, entry.item]));
    for (let slot = 0; slot < this.inventory.bagSize; slot += 1) {
      const cell = document.createElement('div');
      cell.className = slot === this.moving ? 'slot slot--moving' : 'slot';
      this.bindDropTarget(cell, { kind: 'bag', slot });
      // The touch move's second tap: wherever it lands in the bag, as a drop there.
      cell.addEventListener('click', () => {
        const from = this.moving;
        if (from === null) return;
        this.endMove();
        if (from !== slot) this.options.onMove(from, slot);
      });
      const instance = bySlot.get(slot);
      if (instance !== undefined) {
        cell.append(this.itemElement(instance, { from: 'bag', slot, itemId: instance.id }));
      }
      this.bagGrid.append(cell);
    }

    this.renderWardrobe();

    this.purse.replaceChildren(
      kitChip({ icon: 'gold', label: 'Or', value: uiNumber(this.inventory.gold) }),
    );
    this.sortButton.update({ picking: this.pick !== null, empty: this.inventory.bag.length === 0 });
    this.destroyButton.update({
      picking: this.pick !== null,
      empty: this.inventory.bag.length === 0,
      armed: this.destroying,
    });
    // The wardrobe keeps the order things came in: nothing to tidy there.
    this.bagHeading.replaceChildren(
      ...(this.side === 'look'
        ? [kitHeading('Garde-robe', String(this.inventory.wardrobe.length))]
        : [
            kitHeading(
              'Sac',
              `${String(this.inventory.bag.length)} / ${String(this.inventory.bagSize)}`,
            ),
            this.sortButton.element,
            this.destroyButton.element,
          ]),
    );
    this.seal.replaceChildren(kitSeal(this.progress?.level ?? 1));
    this.preview.draw(equipped);
    this.renderStats(equipped);
    // The pinned card outlives a redraw (a kill's XP), not its item.
    if (this.selected !== null && this.root.querySelector('.item--selected') === null) {
      this.tooltip.hide();
    }
  }

  /**
   * The wardrobe's cases: every look and companion owned, grouped by the
   * slot they go to, then in the order they came. No empty case to aim at:
   * it has no order to change, only a last row filled out.
   */
  private renderWardrobe(): void {
    this.wardrobeGrid.replaceChildren();
    const order = (templateId: string): number => {
      const slot = getItem(templateId)?.slot;
      return slot === undefined ? ITEM_SLOTS.length : ITEM_SLOTS.indexOf(slot);
    };
    const entries = [...this.inventory.wardrobe].sort(
      (a, b) => order(a.item.templateId) - order(b.item.templateId) || a.slot - b.slot,
    );
    const cases = Math.max(
      WARDROBE_MIN_CASES,
      Math.ceil(entries.length / WARDROBE_COLUMNS) * WARDROBE_COLUMNS,
    );
    for (let index = 0; index < cases; index += 1) {
      const cell = element('div', 'slot');
      const instance = entries[index]?.item;
      if (instance !== undefined) {
        cell.append(this.itemElement(instance, { from: 'wardrobe', itemId: instance.id }));
      }
      this.wardrobeGrid.append(cell);
    }
    this.wardrobeHint.hidden = this.side !== 'look' || entries.length > 0;
  }

  private renderStats(equipped: Equipped): void {
    const level = this.progress?.level ?? 1;
    const definition = CLASSES[this.options.classId];
    const bonus = equipmentBonus(wornPieces(equipped, getItem));
    const stats = statsFor(definition, level, bonus);
    // What the armour is worth in a fight of the player's own level: a
    // number of points means little until it reads as blows taken.
    const absorbed = Math.round(mitigation(stats.defence, level) * 100);
    // The outfit's average upgrade (ADR 0013): the halo's level, « +N » in
    // its colour, then the exact average, so the next level's distance shows.
    const aura = auraLevel(equipped);
    const color = upgradeColor(aura);

    this.statsBlock.replaceChildren(
      kitHeading('Caractéristiques'),
      kitLines([
        {
          icon: 'stat-health',
          label: 'Vie',
          value: String(stats.maxHealth),
          note: bonus.health > 0 ? `+${String(bonus.health)}` : undefined,
          title: 'Vie maximale ; en vert, ce que donne l’équipement',
        },
        // The total a fight feels, then the outfit's share of it: what a
        // piece swapped in or out moves.
        // The average blow, crits included (ADR 0057); the outfit's share in green.
        {
          icon: 'stat-dps',
          label: 'DPS',
          value: formatDps(effectiveDps(stats.dps, combatOfEquipped(equipped))),
          note: `+${formatDps(bonus.dps)}`,
          title:
            'Dégâts par seconde en moyenne, critiques compris ; en vert, la part de l’équipement',
        },
        {
          icon: 'stat-defence',
          label: 'Défense',
          value: String(stats.defence),
          note: absorbed > 0 ? `-${String(absorbed)} %` : undefined,
          title: uiFormat('En vert, les dégâts retirés aux coups d’un monstre de niveau {level}', {
            level,
          }),
        },
        {
          icon: 'stat-upgrade',
          label: 'Amélioration',
          value: `+${String(aura)}`,
          note: `(${uiDecimal(upgradeAverage(equipped), 1)})`,
          quietNote: true,
          tone: color === undefined ? undefined : `#${color.toString(16).padStart(6, '0')}`,
          title: uiFormat(
            'Moyenne des {count} emplacements d’équipement : le halo s’allume à +{shine}',
            { count: AURA_SLOTS.length, shine: SHINE_FROM_LEVEL },
          ),
        },
        ...combatLines(equipped, stats.dps),
      ]),
    );
  }

  private itemElement(instance: ItemInstance, source: DragSource): HTMLElement {
    const template = getItem(instance.templateId);
    const element = document.createElement('div');
    element.className = `item item--${template?.rarity ?? 'common'}`;
    // What the camp's coach lights up (ADR 0078).
    element.dataset.template = instance.templateId;
    element.dataset.from = source.from;
    // While another window takes from the bag, a click hands over and nothing
    // else: no drag, and no double-click equipping the piece being confirmed
    // (two clicks).
    // A finger has no drag and drop (iOS has none at all): its card moves items.
    element.draggable = this.pick === null && !isTouch();
    const key = source.from === 'body' ? `body:${source.slot}` : source.itemId;
    if (key === this.selected) element.classList.add('item--selected');
    paintItemIcon(element, template, this.options.icons ?? null, instance.templateId);
    addLevelBadge(element, instance.upgradeLevel);
    if (
      source.from === 'bag' &&
      template !== undefined &&
      isBetterThanWorn(
        { template, upgradeLevel: instance.upgradeLevel, affixes: instance.affixes },
        this.wearer(),
        this.inventory.equipped,
      )
    ) {
      addBetterArrow(element);
    }
    if (source.from !== 'body') this.bindPick(element, instance);
    if (instance.quantity > 1) {
      const quantity = document.createElement('span');
      quantity.className = 'item__qty';
      quantity.textContent = String(instance.quantity);
      element.append(quantity);
    }
    element.addEventListener('dragstart', (event) => {
      event.dataTransfer?.setData('application/x-shefira-item', JSON.stringify(source));
      this.tooltip.hide();
    });
    element.addEventListener('dblclick', () => {
      if (template === undefined || this.pick !== null || isTouch()) return;
      if (source.from === 'body') this.options.onUnequip(source.slot);
      else if (template.kind === 'equipment') this.equipFromBag(source.itemId, template);
      else if (template.kind === 'consumable') this.options.onUse(source.itemId);
    });
    if (template !== undefined) {
      element.addEventListener('click', (event) => {
        if (this.destroying) {
          if (source.from === 'bag') {
            this.endDestroy();
            this.askDestroy(source.itemId);
          }
          return;
        }
        // Shift+click links the item in the chat (ADR 0085); in pick mode Shift hands the stack.
        if (event.shiftKey && !isTouch() && this.pick === null && this.options.onLink) {
          this.tooltip.hide();
          this.options.onLink(instance);
          return;
        }
        // During a touch move the tap belongs to the case under it.
        if (!isTouch() || this.moving !== null) return;
        this.select(element, key, instance, template, source);
      });
      // A tap also fires emulated mouse events: they would open the cursor's
      // card, then close the pinned one as the finger reaches its buttons.
      element.addEventListener('mouseenter', (event) => {
        if (isTouch()) return;
        this.tooltip.show(
          template,
          instance,
          this.wearer(),
          { x: event.clientX, y: event.clientY },
          this.details(instance, template, source),
        );
      });
      element.addEventListener('mousemove', (event) => {
        if (isTouch()) return;
        this.tooltip.moveTo({ x: event.clientX, y: event.clientY });
      });
      element.addEventListener('mouseleave', () => {
        if (isTouch()) return;
        this.tooltip.hide();
      });
    }
    return element;
  }

  /** A piece in the bag is set against the one worn in its slot; a worn one has nothing to face. */
  private details(
    instance: ItemInstance,
    template: ItemTemplate,
    source: DragSource,
  ): TooltipDetails {
    if (source.from === 'body') {
      // A worn piece: its affixes against the ceilings of everything worn (ADR 0057).
      return {
        wornTotals: affixTotals(
          wornPieces(this.inventory.equipped, getItem).map((piece) => piece.affixes),
        ),
      };
    }
    const compare = compareWithWorn(
      template,
      this.inventory.equipped,
      instance.upgradeLevel,
      instance.affixes,
    );
    return compare === undefined ? {} : { compare };
  }

  /** A tap on an item: its card pinned beside it, with what the mouse gestures would do. */
  private select(
    element: HTMLElement,
    key: string,
    instance: ItemInstance,
    template: ItemTemplate,
    source: DragSource,
  ): void {
    this.root.querySelector('.item--selected')?.classList.remove('item--selected');
    this.tooltip.pin(
      template,
      instance,
      this.wearer(),
      element,
      this.touchActions(instance, template, source),
      () => {
        this.selected = null;
        this.root.querySelector('.item--selected')?.classList.remove('item--selected');
      },
      this.details(instance, template, source),
    );
    this.selected = key;
    element.classList.add('item--selected');
  }

  /**
   * The card's buttons, one per mouse gesture: double-click (equip, remove,
   * use), a drag to another bag case (move), and in pick mode the click and
   * its Shift. The tap then the button is the precious item's second click.
   */
  private touchActions(
    instance: ItemInstance,
    template: ItemTemplate,
    source: DragSource,
  ): TooltipAction[] {
    const pick = this.pick;
    if (pick !== null) {
      if (source.from === 'body') return [];
      if (pick.canPick !== undefined && !pick.canPick(instance, template)) return [];
      const hand = (quantity: number) => () => {
        if (this.pick === pick) pick.onPick(instance.id, quantity);
      };
      if (instance.quantity <= 1) return [{ label: pick.label, run: hand(1) }];
      return [
        { label: uiFormat('{label} 1', { label: uiText(pick.label) }), run: hand(1) },
        {
          label: uiFormat('Tout (×{count})', { count: instance.quantity }),
          run: hand(instance.quantity),
        },
      ];
    }
    const onLink = this.options.onLink;
    const link: TooltipAction[] =
      onLink === undefined
        ? []
        : [
            {
              label: 'Lier dans le chat',
              run: () => {
                onLink(instance);
              },
            },
          ];
    if (source.from === 'body') {
      return [
        {
          label: 'Retirer',
          run: () => {
            this.options.onUnequip(source.slot);
          },
        },
        ...link,
      ];
    }
    const actions: TooltipAction[] = [];
    if (template.kind === 'equipment') {
      actions.push({
        label: 'Équiper',
        run: () => {
          this.equipFromBag(source.itemId, template);
        },
      });
    } else if (template.kind === 'consumable') {
      actions.push({
        label: 'Utiliser',
        run: () => {
          this.options.onUse(source.itemId);
        },
      });
    }
    // The wardrobe has no order to change (ADR 0072).
    if (source.from === 'bag') {
      actions.push({
        label: 'Déplacer',
        run: () => {
          this.startMove(source.slot);
        },
      });
      actions.push({
        label: 'Détruire',
        run: () => {
          this.askDestroy(source.itemId);
        },
      });
    }
    actions.push(...link);
    return actions;
  }

  /** The touch drag: the next tap on a bag case drops the item there, a tap elsewhere cancels. */
  private startMove(slot: number): void {
    this.moving = slot;
    this.root.classList.add('inventory--moving');
    this.pickHint.hidden = false;
    this.pickHint.textContent = uiText('Touche la case où poser l’objet.');
    document.addEventListener('pointerdown', this.onMoveOutside, true);
    this.render();
  }

  private endMove(): void {
    if (this.moving === null) return;
    this.moving = null;
    this.root.classList.remove('inventory--moving');
    document.removeEventListener('pointerdown', this.onMoveOutside, true);
    this.showPickHint();
    this.render();
  }

  /** « Détruire » armed: the next bag item clicked goes to the confirmation, a click elsewhere cancels. */
  private startDestroy(): void {
    this.endMove();
    this.tooltip.hide();
    this.destroying = true;
    this.root.classList.add('inventory--destroying');
    this.pickHint.hidden = false;
    this.pickHint.textContent = uiText(
      isTouch() ? 'Touche l’objet à détruire.' : 'Clique l’objet à détruire (Échap : annuler).',
    );
    document.addEventListener('pointerdown', this.onDestroyOutside, true);
    this.render();
  }

  private endDestroy(): void {
    if (!this.destroying) return;
    this.destroying = false;
    this.root.classList.remove('inventory--destroying');
    document.removeEventListener('pointerdown', this.onDestroyOutside, true);
    this.showPickHint();
    this.render();
  }

  /** The confirmation window, then the intent; the server answers with the bag or a refusal. */
  private askDestroy(itemId: string): void {
    const instance = this.inventory.bag.find((entry) => entry.item.id === itemId)?.item;
    const template = instance === undefined ? undefined : getItem(instance.templateId);
    if (instance === undefined || template === undefined) return;
    this.tooltip.hide();
    void confirmItemDestruction(template, instance, this.options.icons ?? null).then(
      (quantity) => {
        if (quantity !== null) this.options.onDestroy(itemId, quantity);
      },
    );
  }

  /** In pick mode, a click hands the item over (see `setPickMode`); otherwise it does nothing. */
  private bindPick(element: HTMLElement, instance: ItemInstance): void {
    const template = getItem(instance.templateId);
    const pick = this.pick;
    if (pick === null || template === undefined) return;
    if (pick.canPick !== undefined && !pick.canPick(instance, template)) {
      element.classList.add('item--unpickable');
      return;
    }
    if (this.armed?.itemId === instance.id && this.armed.until > Date.now()) {
      element.classList.add('item--armed');
      element.title = uiFormat('Clique encore pour {verb}', {
        verb: uiText(pick.label).toLowerCase(),
      });
    }
    element.addEventListener('click', (event) => {
      // A finger picks through the item's card (`touchActions`).
      if (this.pick !== pick || isTouch()) return;
      const precious =
        instance.upgradeLevel > 0 || rarityRank(template.rarity) >= rarityRank('epic');
      const armed = this.armed?.itemId === instance.id && this.armed.until > Date.now();
      if (precious && !armed) {
        this.armed = { itemId: instance.id, until: Date.now() + PICK_CONFIRM_MS };
        this.render();
        window.setTimeout(() => {
          if (this.armed?.itemId !== instance.id) return;
          this.armed = undefined;
          this.render();
        }, PICK_CONFIRM_MS);
        return;
      }
      this.armed = undefined;
      this.tooltip.hide();
      // One at a time by default: a stray click never empties a stack.
      pick.onPick(instance.id, event.shiftKey ? instance.quantity : 1);
    });
  }

  private bindDropTarget(
    cell: HTMLElement,
    target: { kind: 'bag'; slot: number } | { kind: 'wardrobe' } | { kind: 'body'; slot: BodySlot },
  ): void {
    cell.addEventListener('dragover', (event) => {
      event.preventDefault();
      cell.classList.add('slot--over');
    });
    cell.addEventListener('dragleave', () => {
      cell.classList.remove('slot--over');
    });
    cell.addEventListener('drop', (event) => {
      event.preventDefault();
      cell.classList.remove('slot--over');
      const raw = event.dataTransfer?.getData('application/x-shefira-item');
      if (raw === undefined || raw === '') return;
      const source = JSON.parse(raw) as DragSource;
      if (target.kind === 'bag' || target.kind === 'wardrobe') {
        // The server takes a worn piece off to where it belongs, bag or wardrobe.
        if (source.from === 'body') {
          this.options.onUnequip(source.slot);
        } else if (target.kind === 'bag' && source.from === 'bag' && source.slot !== target.slot) {
          this.options.onMove(source.slot, target.slot);
        }
      } else if (source.from !== 'body') {
        this.equipFromBag(source.itemId, getItem(this.templateOf(source.itemId) ?? ''));
      }
    });
  }

  private templateOf(itemId: string): string | undefined {
    return carriedEntries(this.inventory).find((entry) => entry.item.id === itemId)?.item
      .templateId;
  }

  /**
   * Ask to wear a bag item; the server decides where it goes. A cosmetic goes
   * to the look side, so the doll turns to it and the player sees it land.
   */
  private equipFromBag(itemId: string, template: ItemTemplate | undefined): void {
    this.options.onEquip(itemId);
    if (template?.cosmetic === true && this.side !== 'look') this.showSide('look');
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('pointerdown', this.onMoveOutside, true);
    document.removeEventListener('pointerdown', this.onDestroyOutside, true);
    this.tooltip.destroy();
    this.button.remove();
    this.root.remove();
  }
}
